import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { RxArrowDown } from "react-icons/rx";
import { LiquidationAggregation } from "types";
import { MovementTypes, STATUS_CODES } from "constants/index";
import { useMemo, useState } from "react";
import { CustomHeadCell } from "components/utils/LiquidationDetails";
import { arsFormatter } from "helpers";
import Row from "./AdditionalsDetailRow";
import { axiosPut } from "api";
import { LIQUIDATIONS_URL } from "constants/urls";
import ConfirmAdditionalItemChangeModal from "./ConfirmAdditionalChangeModal";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import { ENTITY } from "constants/entities";

type Props = {
  print?: boolean;
  liquidation: LiquidationAggregation;
  hasDetail: boolean;
  refetchLiquidation?: () => Promise<any>;
};

export default function AdditionalDetailTable({
  print = false,
  liquidation,
  hasDetail,
  refetchLiquidation,
}: Props) {
  const [pendingChanges, setPendingChanges] = useState<any[]>([]);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [changePreview, setChangePreview] = useState<{
    description?: string;
    fieldLabel?: string;
    value?: string | number;
  } | null>(null);
  const theme = useTheme();
  const { showSnackBar } = useToast();
  const { additionalItems, updateSessionData } = useSession();

  const movements = useMemo(() => {
    const incomeAndDischargesWithAdditionals = new Set();
    liquidation.liquidation_movement_additional_items.forEach((m) =>
      incomeAndDischargesWithAdditionals.add(m.movement_id),
    );
    return liquidation.liquidation_movements.filter(
      (m) =>
        m.movement_type_id === MovementTypes.Additional ||
        incomeAndDischargesWithAdditionals.has(m.movement_id),
    );
  }, [liquidation]);

  const handleSave = () => {
    if (!pendingChanges.length) return;

    const firstChange = pendingChanges[0];

    const additionalItem = liquidation.liquidation_additional_items.find(
      (item) => item.additional_item_id === firstChange.additional_item_id,
    );

    let fieldLabel = "valor";
    let value: string | number | undefined = undefined;

    if (firstChange.amount !== undefined) {
      fieldLabel = "cantidad";
      value = firstChange.amount;
    } else if (firstChange.price !== undefined) {
      fieldLabel = "precio";
      value = firstChange.price;
    } else if (firstChange.subtotal !== undefined) {
      fieldLabel = "subtotal";
      value = firstChange.subtotal;
    }

    setChangePreview({
      description: additionalItem?.description,
      fieldLabel,
      value,
    });

    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    try {
      const response = await axiosPut(
        `${LIQUIDATIONS_URL}${liquidation.id}/additional-items/bulk`,
        pendingChanges,
        true,
      );

      if (response.status === STATUS_CODES.OK) {
        const priceChanges = pendingChanges.filter(
          (change) => change.price !== undefined,
        );

        priceChanges.forEach((change) => {
          const currentAdditionalItem = additionalItems.find(
            (item) => item.id === change.additional_item_id,
          );

          if (currentAdditionalItem) {
            updateSessionData(ENTITY.ADDITIONAL_ITEMS, {
              ...currentAdditionalItem,
              price: change.price,
            });
          }
        });

        setPendingChanges([]);
        setConfirmOpen(false);
        setChangePreview(null);

        showSnackBar(
          `${pendingChanges.length} adicional(es) actualizado(s) correctamente`,
          "success",
        );

        await refetchLiquidation?.();
      }
    } catch (error) {
      showSnackBar("Ocurrió un error al actualizar los adicionales", "error");
    }
  };

  const handleChange = (change: any) => {
    setPendingChanges((prev) => {
      const index = prev.findIndex(
        (c) =>
          c.additional_item_id === change.additional_item_id &&
          c.movement_id === change.movement_id,
      );

      if (index !== -1) {
        const updated = [...prev];
        updated[index] = { ...updated[index], ...change };
        return updated;
      }

      return [...prev, change];
    });
  };

  return (
    <Grid item xs={12}>
      <Accordion defaultExpanded={print}>
        <AccordionSummary
          expandIcon={
            !print && (
              <RxArrowDown style={{ color: theme.palette.primary.main }} />
            )
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography variant="h6">Detalle de Adicionales</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} elevation={4}>
            <Table size="small" aria-label="collapsible table">
              <TableHead
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                })}
              >
                <TableRow>
                  <CustomHeadCell></CustomHeadCell>
                  <CustomHeadCell>Tipo Mov.</CustomHeadCell>
                  <CustomHeadCell>Nº Mov</CustomHeadCell>
                  <CustomHeadCell>Doc. Cliente</CustomHeadCell>
                  <CustomHeadCell>Fecha</CustomHeadCell>
                  <CustomHeadCell>Cliente Asoc.</CustomHeadCell>
                  <CustomHeadCell>Subtotal</CustomHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement, index) => (
                  <Row
                    pendingChanges={pendingChanges}
                    onChange={handleChange}
                    key={index}
                    row={movement}
                    liquidation={liquidation}
                    print={print}
                    hasDetail={hasDetail}
                  />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={13} align="right">
                    <Typography variant="h6">
                      Total Adicionales:{" "}
                      {arsFormatter(liquidation.subtotal_adicionales)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <Button
            sx={{
              marginTop: 2,
            }}
            variant="contained"
            onClick={handleSave}
            disabled={!pendingChanges.length}
          >
            Guardar cambios
          </Button>
        </AccordionDetails>
      </Accordion>
      <ConfirmAdditionalItemChangeModal
        open={confirmOpen}
        handleOpen={() => {
          setConfirmOpen(!confirmOpen);
          if (confirmOpen) {
            setChangePreview(null);
          }
        }}
        handleConfirm={handleConfirmSave}
        description={changePreview?.description}
        fieldLabel={changePreview?.fieldLabel}
        value={changePreview?.value}
      />
    </Grid>
  );
}
